import React from 'react';
import { Plus } from 'lucide-react';
import { SecondaryButton } from '@/styles/StandardButtons';
import { ItemSelectionMenu } from '@/styles/ItemSelectionMenu';
import type { GenericAttachment } from '@/styles/GenericAttachmentsSection';
import { isTempId } from './store';
import { SelectionOptionCard } from './SelectionOptionCard';

/** An option as the form edits it (prices are strings while typing). */
export interface EditableOption {
  id: string;
  name: string;
  description: string;
  link: string;
  builder_price: string;
  client_price: string;
}

export const blankOption = (id: string): EditableOption => ({ id, name: '', description: '', link: '', builder_price: '', client_price: '' });

interface SelectionOptionsEditorProps {
  options: EditableOption[];
  onAdd: () => void;
  /** The option's title: opens it in its own modal. */
  onOpen: (optionId: string) => void;
  selectedOptionIds: string[];
  /** Persisted options can be selected right here (auto-saved); unsaved ones can't yet. */
  onToggleSelected: (optionId: string, selected: boolean) => void;
  togglingOptionId: string | null;
  /** The first picture of an option's files (its card's photo). */
  thumbnailOf: (optionId: string) => GenericAttachment | undefined;
  /** The option's Builder Price (the total of its items); undefined for an audience that may not see it. */
  builderPriceOf: (optionId: string) => number | undefined;
  /** Selection mode (Select / Copy / Delete): the checkboxes only mark options for an action -- they are independent of which options are approved. */
  selection: {
    active: boolean;
    checkedIds: string[];
    onEnter: () => void;
    onExit: () => void;
    onToggleChecked: (optionId: string, checked: boolean) => void;
    onCopy: () => void;
    onDelete: () => void;
  };
}

const toNumber = (value: string) => (value === '' ? 0 : parseFloat(value) || 0);

/**
 * The options part of the Selection form: one card per option (photo, title, summary, link and Client Price -- never the Builder Price), for looking at them.
 * Nothing is edited on a card: its title opens the option in its own modal (SelectionOptionModal), where its data and its items are edited. A persisted
 * option can be accepted / deselected right on its card (auto-saved).
 */
export function SelectionOptionsEditor({ options, onAdd, onOpen, selectedOptionIds, onToggleSelected, togglingOptionId, thumbnailOf, builderPriceOf, selection }: SelectionOptionsEditorProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-semibold">Options</h4>
        <div className="flex items-center gap-2">
          <ItemSelectionMenu
            isSelectMode={selection.active}
            hasSelection={selection.checkedIds.length > 0}
            onEnterSelectMode={selection.onEnter}
            onExitSelectMode={selection.onExit}
            onCopy={selection.onCopy}
            onDelete={selection.onDelete}
          >
            <SecondaryButton type="button" onClick={onAdd}>
              <Plus size={16} className="mr-2" />
              Add Option
            </SecondaryButton>
          </ItemSelectionMenu>
        </div>
      </div>

      {options.length === 0 && <p className="text-body-small-muted">No options yet. Add the choices the client can pick from.</p>}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {options.map((option) => (
          <SelectionOptionCard
            key={option.id}
            option={{
              id: option.id,
              name: option.name || 'Untitled option',
              description: option.description || null,
              link: option.link || null,
              client_price: toNumber(option.client_price),
              is_selected: selectedOptionIds.includes(option.id),
            }}
            thumbnail={thumbnailOf(option.id)}
            builderPrice={builderPriceOf(option.id)}
            selectable={selection.active ? { checked: selection.checkedIds.includes(option.id), onChange: (checked) => selection.onToggleChecked(option.id, checked) } : undefined}
            onOpen={() => onOpen(option.id)}
            onToggle={isTempId(option.id) ? undefined : (card) => onToggleSelected(card.id, !card.is_selected)}
            disabled={togglingOptionId === option.id}
          />
        ))}
      </div>
    </div>
  );
}
